import { Button, Input } from '30pay/components';
import { ConfigProvider } from 'antd';
import { LevelOne, ReduxState } from 'interfaces';
import React, { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { SiPayModal } from 'utilities/components';
import { __VerifyModalProps } from './verifyModal.interface';
import { FaSquareCheck } from 'react-icons/fa6';
import { DatePicker, JalaliLocaleListener } from 'antd-jalali';
import fa_IR from 'antd/locale/fa_IR';
import { convertFaToEnCalendar2, faNumToEnNum } from 'scripts';
import dayjs from 'dayjs';
import { requestUpgradeTo1, verifyUpgradeTo1 } from './verifyModal.service';
import moment from 'jalali-moment';

enum Stage {
    Initial,
    Result,
}

const ProfileVerifyModal: React.FC<ConnectedProps<typeof connector> & __VerifyModalProps> = function (
    props: ConnectedProps<typeof connector> & __VerifyModalProps,
) {
    const [stage, setStage] = useState<Stage>(Stage.Initial);
    const [data, setData] = useState<{ nationalCode?: string; birthDate?: dayjs.Dayjs | null }>();
    const [result, setResult] = useState<LevelOne | undefined>();
    const [loading, setLoading] = useState<boolean>(false);

    const onRequest = () => {
        const jalaliDate = data?.birthDate?.format('YYYY/MM/DD');

        const payload = {
            nationalCode: data?.nationalCode,
            birthDate: convertFaToEnCalendar2(jalaliDate!),
        };

        requestUpgradeTo1(setLoading, payload, setResult, () => setStage(Stage.Result));
    };

    const onVerify = () => {
        setLoading(true);
        verifyUpgradeTo1({ isConfirmed: true })
            .then(() => props.onClose(true))
            .finally(() => setLoading(false));
    };

    const renderTitle = () => {
        switch (stage) {
            case Stage.Initial:
                return 'احراز پایه';
            case Stage.Result:
                return 'تایید احراز پایه';
            default:
                return '';
        }
    };

    return (
        <SiPayModal isOpen={props.isOpen} onClose={props.onClose} title={renderTitle()}>
            {stage === Stage.Initial && (
                <div className="flex flex-col gap-6">
                    <div className="flex items-center gap-4">
                        <Input
                            maxLength={10}
                            placeholder="کد ملی خود را وارد کنید"
                            label="کد ملی:"
                            value={data?.nationalCode}
                            onChange={(e) => setData({ ...data, nationalCode: faNumToEnNum(e.target.value) })}
                        />
                        <div className={'flex flex-col w-full'}>
                            <label className="font-bold text-base text-black-100 mb-2 max-lg:text-sm">
                                تاریخ تولد:
                            </label>
                            <ConfigProvider locale={fa_IR} direction={'rtl'}>
                                <JalaliLocaleListener />
                                <DatePicker
                                    className={
                                        'border border-primary-40 rounded-[28px] h-14 max-lg:h-12 text-base max-lg:text-sm px-3'
                                    }
                                    placeholder={'تاریخ تولد خود را انتخاب کنید'}
                                    onChange={(date: any) => {
                                        date !== null &&
                                            setData({
                                                ...data,
                                                birthDate: date,
                                            });
                                    }}
                                />
                            </ConfigProvider>
                        </div>
                    </div>
                    <div className="flex flex-col gap-4">
                        <label className="text-base font-bold">مزایای احراز هویت پایه</label>
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                                <FaSquareCheck size={16} className="text-positive-dark" />
                                <p className="font-medium text-xs text-darkPurple-100">برداشت تومانی</p>
                            </div>
                            <div className="flex items-center gap-1">
                                <FaSquareCheck size={16} className="text-positive-dark" />
                                <p className="font-medium text-xs text-darkPurple-100">واریز تومانی</p>
                            </div>
                            <div className="flex items-center gap-1">
                                <FaSquareCheck size={16} className="text-positive-dark" />
                                <p className="font-medium text-xs text-darkPurple-100">واریز رمزارز</p>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-4 w-full">
                        <Button
                            className="w-full"
                            type="primary"
                            loading={loading}
                            disabled={loading || !data?.nationalCode || !data.birthDate}
                            onClick={onRequest}
                        >
                            تایید اطلاعات
                        </Button>
                        <Button onClick={() => props.onClose()} type="text" className="bg-background w-full">
                            انصراف
                        </Button>
                    </div>
                </div>
            )}
            {stage === Stage.Result && (
                <div className="flex flex-col gap-6">
                    <div className="grid grid-cols-2 gap-4">
                        {result?.nationalCode && (
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-base text-black-100">کد ملی:</span>
                                <span className="font-medium text-base text-black-60">{result.nationalCode}</span>
                            </div>
                        )}
                        {result?.fullName && (
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-base text-black-100">نام و نام خانوادگی:</span>
                                <span className="font-medium text-base text-black-60">{result.fullName}</span>
                            </div>
                        )}
                        {result?.fatherName && (
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-base text-black-100">نام پدر:</span>
                                <span className="font-medium text-base text-black-60">{result.fatherName}</span>
                            </div>
                        )}
                        {result?.birthDate && (
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-base text-black-100">تاریخ تولد:</span>
                                <span className="font-medium text-base text-black-60">
                                    {moment.utc(result.birthDate).locale('fa').local().format('jD jMMMM jYYYY')}
                                </span>
                            </div>
                        )}
                        {result?.gender && (
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-base text-black-100">جنسیت:</span>
                                <span className="font-medium text-base text-black-60">{result.gender}</span>
                            </div>
                        )}
                        {result?.email && (
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-base text-black-100">ایمیل:</span>
                                <span className="font-medium text-base text-black-60">{result.email}</span>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-4 w-full">
                        <Button type="primary" className="w-full" loading={loading} onClick={onVerify}>
                            تایید
                        </Button>
                        <Button type="text" className="bg-background w-full" onClick={() => props.onClose()}>
                            انصراف
                        </Button>
                    </div>
                </div>
            )}
        </SiPayModal>
    );
};

const mapStateToProps = (state: ReduxState) => ({
    isAuth: state.authStatus,
    user: state.userData,
});

const connector = connect(mapStateToProps);
export default connector(ProfileVerifyModal);
